import { gql } from '@/utilities/gql';
import type { QueryFunctionContext } from '@tanstack/react-query';

import { gqlClient } from '@/utilities/graphQLClient';
import type { CountryISO } from '@/constants/countries';

enum Fields {
  return_7d = 'return7D',
  return_1yr_abs = 'return1YrAbs',
  return_90d = 'return90D',
  return_ytd = 'returnYTD',
}

export const getDefaultAverages = () => ({
  return7D: null,
  return1YrAbs: null,
  return90D: null,
  returnYTD: null,
  latestAnalysis: null,
});

export const QUERY_KEY_INDUSTRY_METRICS = ['INDUSTRY_METRICS'] as const;

type QueryKey = [string, CountryISO, number];
export const getQueryKey = (
  market: CountryISO,
  industryId: number
): QueryKey => [...QUERY_KEY_INDUSTRY_METRICS, market, industryId];

export const queryFn = async ({
  queryKey: [_key, countryIso2, industryId = 0],
}: QueryFunctionContext<QueryKey>) => {
  try {
    if (countryIso2 !== null && industryId !== null) {
      const response = await gqlClient.request(
        gql(`
          query getIndustryMetrics($countryIso2: String!, $industryId: Float!) {
            IndustryAverages(
              countryIso2: $countryIso2
              industryId: $industryId
              fields: [
                "return_7d"
                "return_90d"
                "return_1yr_abs"
                "return_ytd"
              ]
            ) {
              name
              value
            }
            IndustryLatestAnalysis(
              countryIso2: $countryIso2
              industryId: $industryId
            ) {
              marketPE
              analysisDate
              companyCount
            }
          }
        `),
        {
          countryIso2: countryIso2.toUpperCase(),
          industryId: Number(industryId),
        }
      );

      const { IndustryAverages, IndustryLatestAnalysis } = response;

      const industryAverage = IndustryAverages.reduce((a, b) => {
        a[Fields[b.name]] = b.value;
        return a;
      }, getDefaultAverages());
      return {
        ...industryAverage,
        latestAnalysis: IndustryLatestAnalysis,
      };
    }
    return getDefaultAverages();
  } catch (error) {
    if (error instanceof Error) {
      console.error(error.message);
      throw new Error(error.message);
    }
  }
};
